import {
  Box,
  Popover,
  Typography,
  useTheme,
} from "@mui/material";
import { useMeeting, useParticipant } from "@videosdk.live/react-sdk";
import React, { useEffect, useRef, useMemo, useState } from "react";
import { MicOff } from "../../icons";
import { IconButton } from "@mui/material";
import { appThemes, useMeetingAppContext } from "../../MeetingAppContextDef";
import {
  invertColor,
  getRandomColor,
  eventEmitter,
  appEvents,
  nameTructed,
  getQualityScore,
} from "../../utils/common";
import useIsMobile from "../../utils/useIsMobile";
import useIsTab from "../../utils/useIsTab";
import VisibilitySensor from "react-visibility-sensor";
import useResponsiveSize from "../../utils/useResponsiveSize";
import Lottie from "react-lottie";
import animationData from "../../animations/equaliser.json";
import circleRipple from "../../animations/circleRipple.json";
import { Pin } from "../../icons";
import useIsLGDesktop from "../../utils/useIsLGDesktop";
import ReactPlayer from "react-player";
import NetworkIcon from "../../icons/NetworkIcon";
import { CloseOutlined } from "@mui/icons-material";
import { useMediaQuery } from "react-responsive";
import {
  calculateFontSize,
  getResponsivePadding,
  calculatePosition,
  calculateAnalyzerSize,
} from "../../utils/styleUtils";


export const CornerDisplayName = ({
  participantId,
  isPresenting,
  displayName,
  isLocal,
  micOn,
  isActiveSpeaker,
  webcamOn,
  pinState,
  pin,
  unpin,
  mouseOver,
  isRecorder,
  isPortrait,
}) => {
  const theme = useTheme();

  const isMobile = useIsMobile();
  const isTab = useIsTab();
  const isLGDesktop = useIsLGDesktop();

  const [downArrow, setDownArrow] = useState(null);
  const handleClick = (event) => {
    setDownArrow(event.currentTarget);
  };

  const handleClose = () => {
    setDownArrow(null);
  };

  // const {
    const overlaidInfoVisible= true;
    const canPin= true;
    const animationsEnabled= true;
    const alwaysShowOverlay= true;
    const networkBarEnabled= true;
    const appTheme= true;
  // } = useMeetingAppContext();

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: animationData,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  const analyzerSize = useResponsiveSize({
    xl: 32,
    lg: 28,
    md: 24,
    sm: 20,
    xs: 18,
  });

  const show = useMemo(
    () =>
      alwaysShowOverlay || mouseOver || isActiveSpeaker || overlaidInfoVisible,
    [alwaysShowOverlay, mouseOver, isActiveSpeaker, overlaidInfoVisible]
  );

  const isPinned = useMemo(() => pinState?.share || pinState?.cam, [pinState]);

  const showPin = useMemo(
    () => (alwaysShowOverlay ? isPinned : isPinned || mouseOver),
    [alwaysShowOverlay, isPinned, mouseOver]
  );

  const displayNameFontSize = calculateFontSize(isRecorder, isMobile, isTab, isPortrait);

  const {
    webcamStream,
    micStream,
    getVideoStats,
    getAudioStats,
    getShareStats,
    screenShareStream,
  } = useParticipant(participantId);

  const statsIntervalIdRef = useRef();
  const [score, setScore] = useState({});
  const [audioStats, setAudioStats] = useState({});
  const [videoStats, setVideoStats] = useState({});

  const updateStats = async () => {
    let stats = [];
    let audioStats = [];
    let videoStats = [];
    if (isPresenting) {
      stats = await getShareStats();
    } else if (webcamStream) {
      stats = await getVideoStats();
    } else if (micStream) {
      stats = await getAudioStats();
    }

    if (webcamStream || micStream || isPresenting) {
      videoStats = isPresenting ? await getShareStats() : await getVideoStats();
      audioStats = isPresenting ? [] : await getAudioStats();
    }

    // setScore(stats?.score);
    let score = stats
      ? stats.length > 0
        ? getQualityScore(stats[0])
        : 100
      : 100;

    setScore(score);
    setAudioStats(audioStats);
    setVideoStats(videoStats);
  };

  const qualityStateArray = [
    { label: "", audio: "Audio", video: "Video" },
    {
      label: "Latency",
      audio:
        audioStats && audioStats[0]?.rtt ? `${audioStats[0]?.rtt} ms` : "-",
      video:
        videoStats && videoStats[0]?.rtt ? `${videoStats[0]?.rtt} ms` : "-",
    },
    {
      label: "Jitter",
      audio:
        audioStats && audioStats[0]?.jitter
          ? `${parseFloat(audioStats[0]?.jitter).toFixed(2)} ms`
          : "-",
      video:
        videoStats && videoStats[0]?.jitter
          ? `${parseFloat(videoStats[0]?.jitter).toFixed(2)} ms`
          : "-",
    },
    {
      label: "Packet Loss",
      audio: audioStats
        ? audioStats[0]?.packetsLost
          ? `${parseFloat(
            (audioStats[0]?.packetsLost * 100) / audioStats[0]?.totalPackets
          ).toFixed(2)}%`
          : "-"
        : "-",
      video: videoStats
        ? videoStats[0]?.packetsLost
          ? `${parseFloat(
            (videoStats[0]?.packetsLost * 100) / videoStats[0]?.totalPackets
          ).toFixed(2)}%`
          : "-"
        : "-",
    },
    {
      label: "Bitrate",
      audio:
        audioStats && audioStats[0]?.bitrate
          ? `${parseFloat(audioStats[0]?.bitrate).toFixed(2)} kb/s`
          : "-",
      video:
        videoStats && videoStats[0]?.bitrate
          ? `${parseFloat(videoStats[0]?.bitrate).toFixed(2)} kb/s`
          : "-",
    },
    {
      label: "Frame rate",
      audio: "-",
      video:
        videoStats &&
          (videoStats[0]?.size?.framerate === null ||
            videoStats[0]?.size?.framerate === undefined)
          ? "-"
          : `${videoStats ? videoStats[0]?.size?.framerate : "-"}`,
    },
    {
      label: "Resolution",
      audio: "-",
      video: videoStats
        ? videoStats && videoStats[0]?.size?.width === null
          ? "-"
          : `${videoStats[0]?.size?.width}x${videoStats[0]?.size?.height}`
        : "-",
    },
    {
      label: "Codec",
      audio: audioStats && audioStats[0]?.codec ? audioStats[0]?.codec : "-",
      video: videoStats && videoStats[0]?.codec ? videoStats[0]?.codec : "-",
    },
    {
      label: "Cur. Layers",
      audio: "-",
      video:
        videoStats && !isLocal
          ? videoStats && videoStats[0]?.currentSpatialLayer === null
            ? "-"
            : `S:${videoStats[0]?.currentSpatialLayer || 0} T:${videoStats[0]?.currentTemporalLayer || 0
            }`
          : "-",
    },
    {
      label: "Pref. Layers",
      audio: "-",
      video:
        videoStats && !isLocal
          ? videoStats && videoStats[0]?.preferredSpatialLayer === null
            ? "-"
            : `S:${videoStats[0]?.preferredSpatialLayer || 0} T:${videoStats[0]?.preferredTemporalLayer || 0
            }`
          : "-",
    },
  ];

  useEffect(() => {
    if (networkBarEnabled) {
      if (webcamStream || micStream || screenShareStream) {
        updateStats();

        if (statsIntervalIdRef.current) {
          clearInterval(statsIntervalIdRef.current);
        }

        statsIntervalIdRef.current = setInterval(updateStats, 500);
      } else {
        if (statsIntervalIdRef.current) {
          clearInterval(statsIntervalIdRef.current);
          statsIntervalIdRef.current = null;
        }
      }

      return () => {
        if (statsIntervalIdRef.current)
          clearInterval(statsIntervalIdRef.current);
      };
    }
  }, [webcamStream, micStream, screenShareStream, networkBarEnabled]);

  const bottomPosition = calculatePosition(show, isMobile, isTab, '0.4vh', '0.8vh', '1.2vh', '-3.2vh');
  const leftPosition = calculatePosition(show, isMobile, isTab, '0.4vh', '0.8vh', '1.2vh', '-4.2vh');
  const paddingTopBottom = getResponsivePadding(isMobile, isTab, '0.2vh', '0.3vh', '0.4vh');
  const paddingLeftRight = getResponsivePadding(isMobile, isTab, '0.4vh', '0.6vh', '0.8vh');

  return (
    <>
      <div
        onClick={(e) => {
          e.stopPropagation();
        }}
        style={{
          position: "absolute",
          bottom: bottomPosition,
          left: leftPosition,
          opacity: show ? 1 : 0,
          paddingTop: paddingTopBottom,
          paddingBottom: paddingTopBottom,
          paddingLeft: paddingLeftRight,
          paddingRight: paddingLeftRight,
          transform: `scale(${show ? 1 : 0})`,
          backgroundColor:
            appTheme === appThemes.LIGHT
              ? theme.palette.lightTheme.three
              : "#00000066",
          borderRadius: '0.6vh',
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: `all ${200 * (animationsEnabled ? 1 : 0.5)}ms`,
          transitionTimingFunction: "linear",
        }}
      >
        <Typography
          variant={isLGDesktop ? "subtitle1" : "subtitle2"}
          style={{
            justifyContent: "center",
            fontSize: displayNameFontSize,
            display: "flex",
            alignItems: "center",
            // lineHeight: 1,
            color:
              appTheme === appThemes.LIGHT ?
                "#95959E" : "white",
          }}
        >
          {isPresenting
            ? isLocal
              ? `You are presenting`
              : `${nameTructed(displayName, 15)} is presenting`
            : isLocal
              ? "You"
              : nameTructed(displayName, 26)}
        </Typography>
        {(!micOn || isActiveSpeaker) && !isPresenting && (
          <div
            onClick={(e) => {
              e.stopPropagation();
            }}
            style={
              isRecorder
                ? null
                : {
                  padding: getResponsivePadding(isMobile, isTab, isActiveSpeaker ? '0vh' : '0.2vh', isActiveSpeaker ? '0vh' : '0.3vh', isActiveSpeaker ? '0vh' : '0.1vh'),
                  backgroundColor: isActiveSpeaker ? "" : "#D32F2Fcc",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: '2.4vh',
                  marginLeft: '0.6vh',
                }
            }
          >
            {isActiveSpeaker ? (
              <Lottie
                options={defaultOptions}
                eventListeners={[{ eventName: "done" }]}
                height={(analyzerSize * 2) / 3}
                width={(analyzerSize * 2) / 2}
                isClickToPauseDisabled
              />
            ) : (
              !isRecorder &&
              !micOn && (
                <MicOff
                  color={appTheme !== appTheme.LIGHT && "white"}
                  style={{
                    color: theme.palette.common.white,
                    height: calculateAnalyzerSize(analyzerSize, isRecorder, isMobile, isPortrait),
                    width: calculateAnalyzerSize(analyzerSize, isRecorder, isMobile, isPortrait),
                  }}
                />
              )
            )}
          </div>
        )}
      </div>
      {canPin && !isRecorder && (
        <div
          className="pinClass"
          style={{
            position: "absolute",
            right: calculatePosition(showPin, isMobile, isTab, 4, 8, 12, -42),
            bottom: calculatePosition(showPin, isMobile, isTab, 4, 8, 12, -32),
            transform: `scale(${showPin ? 1 : 0})`,
            transition: `all ${200 * (animationsEnabled ? 1 : 0.5)}ms`,
            transitionTimingFunction: "linear",
          }}
        >
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              isPinned ? unpin() : pin();
            }}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: getResponsivePadding(isMobile, isTab, '0.3vh', '0.4vh', '0.5vh'),
              backgroundColor: isPinned ? "white" : "#0000004d",
            }}
          >
            <Pin
              fill={isPinned ? "#000" : "#ffffffb3"}
              style={{
                height: analyzerSize * 0.6,
                width: analyzerSize * 0.6,
              }}
            />
          </IconButton>
        </div>
      )}
      {!isRecorder &&
        (webcamStream || micStream || screenShareStream) &&
        networkBarEnabled && (
          <Box>
            <div
              onClick={(e) => {
                e.stopPropagation();
                handleClick(e);
              }}
              style={{
                position: "absolute",
                top: calculatePosition(show, isMobile, isTab, '0.4vh', '0.8vh', '1.2vh', '-3.2vh'),
                right: calculatePosition(show, isMobile, isTab, '0.4vh', '0.8vh', '1.2vh', '-4.2vh'),
                transform: `scale(${show ? 1 : 0})`,
                transition: `all ${200 * (animationsEnabled ? 1 : 0.5)}ms`,
                transitionTimingFunction: "linear",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: getResponsivePadding(isMobile, isTab, '0.3vh', '0.4vh', '0.5vh'),
                backgroundColor:
                  score > 7 ? "#3BA55D" : score > 4 ? "#faa713" : "#FF5D5D",
                borderRadius: '0.4vh',
                cursor: "pointer",
              }}
            >
              <NetworkIcon
                color1={"#ffffff"}
                color2={"#ffffff"}
                color3={"#ffffff"}
                color4={"#ffffff"}
                style={{
                  height: `${analyzerSize * 0.06}vh`,
                  width: `${analyzerSize * 0.06}vh`,
                }}
              />
            </div>
            <div
              style={{
                position: "absolute",
                top: calculatePosition(show, isMobile, isTab, '0.4vh', '0.8vh', '1.2vh', '-3.2vh'),
                right: calculatePosition(show, isMobile, isTab, '0.4vh', '0.8vh', '1.2vh', '-4.2vh'),
              }}
            >
              <Popover
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                anchorEl={downArrow}
                open={Boolean(downArrow)}
                onClose={(e) => {
                  e.stopPropagation();
                  handleClose();
                }}
              >
                <Box>
                  <Box
                    style={{
                      padding: 9,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      backgroundColor:
                        score > 7
                          ? "#3BA55D"
                          : score > 4
                            ? "#faa713"
                            : "#FF5D5D",
                    }}
                  >
                    <Typography
                      variant="body2"
                      style={{ fontWeight: 600 }}
                    >{`Quality Score : ${score > 7 ? "Good" : score > 4 ? "Average" : "Poor"
                      }`}</Typography>

                    <IconButton
                      size="small"
                      style={{ cursor: "pointer" }}
                      onClick={handleClose}
                    >
                      <CloseOutlined style={{ height: 16, width: 16 }} />
                    </IconButton>
                  </Box>
                  <Box style={{ display: "flex" }}>
                    <Box style={{ display: "flex", flexDirection: "column" }}>
                      {qualityStateArray.map((item, index) => {
                        return (
                          <Box
                            style={{
                              display: "flex",
                              borderBottom:
                                index === qualityStateArray.length - 1
                                  ? ""
                                  : `1px solid ${appTheme === appThemes.LIGHT
                                    ? theme.palette.lightTheme.outlineColor
                                    : "#ffffff33"
                                  }`,
                            }}
                          >
                            <Box
                              style={{
                                display: "flex",
                                flex: 1,
                                width: 120,
                                alignItems: "center",
                              }}
                            >
                              {index !== 0 && (
                                <Typography
                                  style={{
                                    fontSize:
                                      isRecorder && isPortrait ? 24 : 12,
                                    marginTop: 6,
                                    marginBottom: 6,
                                    marginLeft: 8,
                                  }}
                                >
                                  {item.label}
                                </Typography>
                              )}
                            </Box>
                            <Box
                              style={{
                                display: "flex",
                                flex: 1,
                                alignItems: "center",
                                justifyContent: "center",
                                borderLeft: `1px solid ${appTheme === appThemes.LIGHT
                                    ? theme.palette.lightTheme.outlineColor
                                    : "#ffffff33"
                                  }`,
                              }}
                            >
                              <Typography
                                style={{
                                  fontSize: isRecorder && isPortrait ? 24 : 12,
                                  marginTop: 6,
                                  marginBottom: 6,
                                  width: 65,
                                  textAlign: "center",
                                }}
                              >
                                {item.audio}
                              </Typography>
                            </Box>
                            <Box
                              style={{
                                display: "flex",
                                flex: 1,
                                alignItems: "center",
                                justifyContent: "center",
                                borderLeft: `1px solid ${appTheme === appThemes.LIGHT
                                    ? theme.palette.lightTheme.outlineColor
                                    : "#ffffff33"
                                  }`,
                              }}
                            >
                              <Typography
                                style={{
                                  fontSize: isRecorder && isPortrait ? 24 : 12,
                                  marginTop: 6,
                                  marginBottom: 6,
                                  width: 65,
                                  textAlign: "center",
                                }}
                              >
                                {item.video}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </Box>
              </Popover>
            </div>
          </Box>
        )}
    </>
  );
};

const TestParticipantViewer = ({ participantId, quality, useVisibilitySensor }) => {
  const videoPlayer = useRef();
  const [videoDivWrapperRef, setVideoDivWrapperRef] = useState(null);
  const [mouseOver, setMouseOver] = useState(false);
  const [portrait, setPortrait] = useState(false);

  const mMeeting = useMeeting();

  const presenterId = mMeeting?.presenterId;

  // const {
  //   appTheme,
  // } = useMeetingAppContext();

  const setOverlaidInfoVisible = true;
  const whiteboardStarted = false;
  const animationsEnabled = false;
  const isRecorder = false;
  const maintainVideoAspectRatio = false;
  const maintainLandscapeVideoAspectRatio = false;
  const isMirrorViewChecked = false;
  const appTheme = 'DARK';

  const isPortrait = useMediaQuery({ query: "(orientation: portrait)" });

  const onStreamEnabled = (stream) => {
    // console.log(participantId, stream.kind, " Stream started ");
  };

  const onStreamDisabled = (stream) => {
    // console.log(participantId, stream.kind, " Stream stopped ");
  };

  const {
    displayName,
    setQuality,
    setViewPort,
    webcamStream,
    webcamOn,
    micOn,
    isLocal,
    isActiveSpeaker,
    pinState,
    pin,
    unpin,
  } = useParticipant(participantId, {
    onStreamDisabled,
    onStreamEnabled,
  });

  const mediaStream = useMemo(() => {
    if (webcamOn && webcamStream) {
      const mediaStream = new MediaStream();
      mediaStream.addTrack(webcamStream.track);
      return mediaStream;
    }
  }, [webcamStream, webcamOn]);

  const participantAccentColor = useMemo(
    () => getRandomColor(appTheme === appThemes.LIGHT ? "dark" : "light"),
    []
  );

  const theme = useTheme();

  useEffect(() => {
    if (!quality || isRecorder) return;

    setQuality(quality);
  }, [quality, setQuality, isRecorder]);

  const dpSize = useResponsiveSize({
    xl: 9.2,
    lg: 5.2,
    md: 5.2,
    sm: 5.2,
    xs: 5.2,
  });

  const flipStyle = useMemo(
    () =>
      isLocal ? { transform: "scaleX(-1)", WebkitTransform: "scaleX(-1)" } : {},
    [isLocal]
  );

  const defaultRippleOptions = {
    loop: true,
    autoplay: true,
    animationData: circleRipple,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  // useEffect(() => {
  //   if (!presenterId) {
  //     typeof webcamStream?.resume === "function" && webcamStream?.resume();
  //   }
  // }, [presenterId, webcamOn, webcamStream]);

  useEffect(() => {
    if (isRecorder) {
      setQuality("high");
    }
  }, [isRecorder]);

  useEffect(() => {
    if (
      videoDivWrapperRef?.offsetWidth &&
      videoDivWrapperRef?.offsetHeight &&
      !isRecorder &&
      !isLocal &&
      webcamStream
    ) {
      // setViewPort(
      //   videoDivWrapperRef?.offsetWidth,
      //   videoDivWrapperRef?.offsetHeight
      // );
    }
  }, [isRecorder, isLocal, videoDivWrapperRef, webcamStream]);

  useEffect(() => {
    eventEmitter.emit(appEvents["participant-visible"], {
      participantId,
    });

    return () => {
      eventEmitter.emit(appEvents["participant-invisible"], {
        participantId,
      });
    };
  }, []);

  const checkAndUpdatePortrait = () => {
    if (webcamStream && maintainVideoAspectRatio) {
      const { height, width } = webcamStream.track.getSettings();
      if (height > width && !portrait) {
        setPortrait(true);
      } else if (height < width && portrait) {
        setPortrait(false);
      }
    } else {
      setPortrait(false);
    }
  };

  return (
    <VisibilitySensor
      active
      // active={!!useVisibilitySensor}
      onChange={(isVisible) => {
        if (isVisible) {
          eventEmitter.emit(appEvents["participant-visible"], {
            participantId,
          });
        } else {
          eventEmitter.emit(appEvents["participant-invisible"], {
            participantId,
          });
        }
        //
        // if (useVisibilitySensor) {
        //   if (isVisible) {
        //     typeof webcamStream?.resume === "function" &&
        //       webcamStream?.resume();
        //   } else {
        //     typeof webcamStream?.pause === "function" && webcamStream?.pause();
        //   }
        // } else {
        //   typeof webcamStream?.resume === "function" && webcamStream?.resume();
        // }
      }}
    >
      <div
        ref={setVideoDivWrapperRef}
        onMouseEnter={() => {
          setMouseOver(true);
        }}
        onMouseLeave={() => {
          setMouseOver(false);
        }}
        onDoubleClick={() => {
          eventEmitter.emit(appEvents["toggle-full-screen"]);
        }}
        onClick={() => {
          setOverlaidInfoVisible((s) => !s);
        }}
        style={{
          height: "100%",
          width: "100%",
          // backgroundColor:
          //   appTheme === appThemes.DARK
          //     ? theme.palette.darkTheme.slightLighter
          //     : appTheme === appThemes.LIGHT
          //     ? theme.palette.lightTheme.two
          //     : theme.palette.background.paper,
          background: '#19193e',
          position: "relative",
          overflow: "hidden",
          borderRadius: isRecorder ? null : '0.8vh',
        }}
        className={`${maintainLandscapeVideoAspectRatio && !portrait
            ? "video-contain"
            : portrait
              ? ""
              : "video-cover"
          }`}
      >
        {webcamOn ? (
          <>
            <ReactPlayer
              ref={videoPlayer}
              //
              playsinline // very very imp prop
              playIcon={<></>}
              //
              pip={false}
              light={false}
              controls={false}
              muted={true}
              playing={true}
              //
              url={mediaStream}
              //
              height={"100%"}
              width={"100%"}
              style={!isMirrorViewChecked && flipStyle}
              onError={(err) => {
                console.log(err, "participant video error");
              }}
              onProgress={() => {
                checkAndUpdatePortrait();
              }}
            />
          </>
        ) : (
          <div
            style={{
              height: "100%",
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isActiveSpeaker && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  right: 0,
                  left: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Lottie
                  options={defaultRippleOptions}
                  eventListeners={[{ eventName: "done" }]}
                  height={`${(dpSize / (presenterId || whiteboardStarted ? 2 : 1)) * 2}vh`}
                  width={`${(dpSize / (presenterId || whiteboardStarted ? 2 : 1)) * 2}vh`}
                  isClickToPauseDisabled
                />
              </div>
            )}

            <Box
              style={{
                zIndex: 10,
                height: `${dpSize / (presenterId || whiteboardStarted ? 2 : 1)}vh`,
                width: `${dpSize / (presenterId || whiteboardStarted ? 2 : 1)}vh`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 100,
                backgroundColor: participantAccentColor,
                transition: animationsEnabled
                  ? "height 800ms, width 800ms"
                  : undefined,
                transitionTimingFunction: "ease-in-out",
              }}
            >
              <Typography
                variant={presenterId || whiteboardStarted ? "body2" : "h5"}
                style={{ color: invertColor(participantAccentColor), fontSize: presenterId || whiteboardStarted ? '1.4vh' : '2.4vh' }}
              >
                {String(displayName).charAt(0).toUpperCase()}
              </Typography>
            </Box>
          </div>
        )}

        <CornerDisplayName
          {...{
            isLocal,
            displayName,
            micOn,
            webcamOn,
            isPresenting: false,
            isActiveSpeaker,
            pin,
            unpin,
            pinState,
            participantId,
            mouseOver,
            isRecorder,
            isPortrait,
          }}
        />
      </div>
    </VisibilitySensor>
  );
};

export default TestParticipantViewer;
